import React, { useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../../config';
import { Mail, CheckCircle2, Loader2 } from 'lucide-react';

const API = `${BACKEND_URL}/api`;

const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setStatus('loading');
    setMessage('');

    try {
      const res = await axios.post(`${API}/newsletter/subscribe`, {
        email: email.trim(),
        name: name.trim() || null,
        source: 'landing',
      });
      const apiStatus = res.data?.status;
      setStatus(apiStatus === 'already_subscribed' ? 'info' : 'success');
      setMessage(res.data?.message || 'Suscripción exitosa');
      if (apiStatus !== 'already_subscribed') {
        setEmail('');
        setName('');
      }
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setMessage(typeof detail === 'string' ? detail : 'No se pudo procesar la suscripción. Intenta de nuevo.');
    }
  };

  return (
    <section
      data-testid="newsletter-section"
      className="relative py-20 px-6 bg-gradient-to-b from-[#1d1e20] via-[#161718] to-[#0f0f10] overflow-hidden"
    >
      {/* Decorative grain */}
      <div
        className="absolute inset-0 opacity-[0.04] pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle at 25% 25%, white 1px, transparent 1px), radial-gradient(circle at 75% 75%, white 1px, transparent 1px)',
          backgroundSize: '40px 40px',
        }}
      />

      <div className="relative max-w-3xl mx-auto text-center">
        <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400/20 to-amber-600/10 border border-amber-400/30 mb-6">
          <Mail className="w-8 h-8 text-amber-400" />
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 tracking-tight">
          Mantente al día con <span className="text-amber-400">JG Models</span>
        </h2>
        <p className="text-base sm:text-lg text-gray-400 mb-10 max-w-xl mx-auto leading-relaxed">
          Recibe oportunidades exclusivas, lanzamientos y consejos de la industria directamente en tu correo. Sin spam, solo lo importante.
        </p>

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-3 max-w-2xl mx-auto"
          data-testid="newsletter-form"
        >
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              placeholder="Tu nombre (opcional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={status === 'loading'}
              data-testid="newsletter-name-input"
              className="flex-1 px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-400/50 focus:bg-white/10 transition-all disabled:opacity-50"
            />
            <input
              type="email"
              placeholder="tu@email.com"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={status === 'loading'}
              data-testid="newsletter-email-input"
              className="flex-1 px-5 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-gray-500 focus:outline-none focus:border-amber-400/50 focus:bg-white/10 transition-all disabled:opacity-50"
            />
          </div>

          <button
            type="submit"
            disabled={status === 'loading' || !email.trim()}
            data-testid="newsletter-submit-button"
            className="group relative px-8 py-4 rounded-xl bg-gradient-to-r from-amber-400 to-amber-500 text-black font-semibold text-base hover:from-amber-300 hover:to-amber-400 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-amber-500/20 hover:shadow-amber-500/40 hover:-translate-y-0.5"
          >
            <span className="flex items-center justify-center gap-2">
              {status === 'loading' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Suscribiendo...
                </>
              ) : status === 'success' ? (
                <>
                  <CheckCircle2 className="w-5 h-5" />
                  ¡Suscrito!
                </>
              ) : (
                'Suscribirme al newsletter'
              )}
            </span>
          </button>
        </form>

        {message && (
          <div
            data-testid="newsletter-message"
            className={`mt-6 px-5 py-3 rounded-xl inline-block text-sm ${
              status === 'success'
                ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300'
                : status === 'info'
                ? 'bg-amber-500/10 border border-amber-500/30 text-amber-300'
                : 'bg-red-500/10 border border-red-500/30 text-red-300'
            }`}
          >
            {message}
          </div>
        )}

        <p className="text-xs text-gray-500 mt-8">
          Respetamos tu privacidad. Puedes cancelar tu suscripción en cualquier momento.
        </p>
      </div>
    </section>
  );
};

export default NewsletterSection;
